import { Table, TableHead, TableRow, Tooltip, TableContainer } from '@mui/material';
import { useFetchPartnersApi } from 'api/mypage/partner';
import { useBulkUpdateProofreadEventApi } from 'api/mypage/proofread_event';
import { FetchProofreadEventsApi } from 'api/mypage/proofread_plans/proofread_event';
import { NewDefectReportFormDialog } from 'components/mypage/defect_report/defect_report_form_dialog';
import AttachedResultEventFormDialog from 'components/mypage/proofread/proofread_event/attached_result_event_form_dialog';
import { CheckboxFooter } from 'components/shared/checkbox_footer';
import ImageView from 'components/shared/image_view';
import { useModal, useMultipleModal } from 'components/shared/modal';
import Pagination from 'components/shared/pagination';
import BulkResultFormDialog from 'components/shared/proofread/bulk_result_form_dialog';
import { InstrumentCategoryFilterTableHeader } from 'components/shared/proofread/instrument_category_filter_table_header';
import { InstrumentInfoFilterTableHeader } from 'components/shared/proofread/instrument_info_filter_table_header';
import {
  ExTableCell,
  ExTableBody,
  ExTableRow,
  AllCheckboxTableCell,
  CheckboxTableCell,
  OrderTableCell,
} from 'components/shared/table';
import { TableExAttributesInfo } from 'components/shared/table_ex_attributes_info';
import ThemedButton from 'components/shared/themed_button';
import { MypageStateContext } from 'contexts/mypage_state_context';
import { ExColumn } from 'entities/ex_column';
import {
  getCommonRegisteredCycle,
  ProofreadEvent,
  ProofreadEventForm,
  ProofreadEventSearchForm,
} from 'entities/proofread_event';
import moment from 'moment';
import { Form, useForm } from 'rac';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MypageRouteHelper } from 'routes/mypage';
import { getTableHeight, PROOFREAD_CHECKED_TABLE_PADDING, PROOFREAD_TABLE_PADDING } from 'utils/component_heights';
import { useCheckboxes } from 'utils/hooks';
import { useProofreadEventFilter } from 'utils/hooks/use_proofread_event_filter';
import { ProofreadEventViewModel } from 'view_models/proofread_event';

type AttachedResultEventTableProps = {
  planId: number;
  indexApi: FetchProofreadEventsApi;
  searchForm: Form<ProofreadEventSearchForm>;
  tableShowColumns: ExColumn[];
  onUpdate: () => void;
  onClickExAttr: (event: ProofreadEvent) => void;
  onClickBulkExAttr: (eventIds: number[]) => void;
};
export const AttachedResultEventTable: React.FC<AttachedResultEventTableProps> = (props) => {
  const proofreadEventFilterSet = useProofreadEventFilter(
    props.indexApi.response.filter,
    props.searchForm,
    props.indexApi
  );
  const { currentUser } = useContext(MypageStateContext);
  const resultModalSet = useMultipleModal<ProofreadEvent>();
  const bulkResultModalSet = useMultipleModal<number[]>();
  const navigate = useNavigate();
  const [prEvent, setPrEvent] = useState<ProofreadEvent>({});
  const suppliersIndexApi = useFetchPartnersApi();
  const newReportModal = useModal();
  const checkboxSet = useCheckboxes(props.indexApi.response.proofreadEvents);
  const [seenEvents, setSeenEvents] = useState<{ [id: number]: ProofreadEvent }>({});
  useEffect(() => {
    setSeenEvents((prev) => {
      const next = { ...prev };
      props.indexApi.response.proofreadEvents.forEach((e) => {
        if (e.id) next[e.id] = e;
      });
      return next;
    });
  }, [props.indexApi.response.proofreadEvents]);

  // 選択機器の登録済み周期が一致していれば初期値として入力し、混在していれば周期の一括変更を不可にする
  const selectedEvents = Array.isArray(bulkResultModalSet.item)
    ? bulkResultModalSet.item.map((id) => seenEvents[id]).filter(Boolean)
    : [];
  const commonCycle = getCommonRegisteredCycle(selectedEvents);

  const patchForm = useForm<ProofreadEventForm>({
    eventIds: bulkResultModalSet.item,
    aasmState: 'fix_result',
    result: 'ok',
    proofreadPlanId: props.planId,
    scheduledDate: moment().toString(),
    proofreadInfoAttributes: commonCycle.mismatch ? {} : { cycle: commonCycle.cycle, cycleUnit: commonCycle.cycleUnit },
  });
  const patchApi = useBulkUpdateProofreadEventApi(patchForm);

  useEffect(() => {
    suppliersIndexApi.execute();
  }, []);

  const handleClickResult = (event: ProofreadEvent) => {
    resultModalSet.openModal(event);
  };
  const handleCompleteResult = () => {
    resultModalSet.closeModal();
    props.onUpdate();
  };

  const handleClickBulkResult = (eventIds: number[]) => {
    // 同一選択で開き直しても item の参照が変わるようコピーを渡す（ダイアログ側の resetForm を確実に発火させる）
    bulkResultModalSet.openModal([...eventIds]);
  };

  const handleCompleteBulkResult = () => {
    bulkResultModalSet.closeModal();
    // 一括更新されたイベントは一覧から消えるため、チェックが残って次回の選択に紛れ込まないよう解除する
    checkboxSet.clear();
    props.onUpdate();
  };

  const handleClickReport = (prEvent: ProofreadEvent) => {
    setPrEvent(prEvent);
    if (prEvent.defectReport) {
      navigate(`${MypageRouteHelper.defectReportShow(prEvent.defectReport.id!)}`);
    } else {
      newReportModal.setOpen(true);
    }
  };
  const handleCompleteReport = () => {
    newReportModal.setOpen(false);
    props.onUpdate();
  };

  return (
    <>
      <AttachedResultEventFormDialog modalSet={resultModalSet} onComplete={handleCompleteResult} />
      <BulkResultFormDialog
        modalSet={bulkResultModalSet}
        onComplete={handleCompleteBulkResult}
        cycleMismatch={commonCycle.mismatch}
        {...{ patchForm, patchApi }}
      />
      <NewDefectReportFormDialog
        modalState={newReportModal}
        instrumentId={prEvent.instrumentId}
        proofreadEventId={prEvent.id}
        onComplete={handleCompleteReport}
      />
      <TableContainer
        style={{
          maxHeight:
            checkboxSet.checkedIds.length > 0
              ? getTableHeight(PROOFREAD_CHECKED_TABLE_PADDING)
              : getTableHeight(PROOFREAD_TABLE_PADDING),
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <AllCheckboxTableCell checkboxSet={checkboxSet} />
              <ExTableCell />
              <ExTableCell>
                <InstrumentInfoFilterTableHeader proofreadEventFilterSet={proofreadEventFilterSet} />
                {props.tableShowColumns.map((col) => (
                  <div style={{ fontSize: 12, lineHeight: '12px' }} key={`tablecol-${col.id}`}>
                    {col.name}
                  </div>
                ))}
              </ExTableCell>
              <ExTableCell align="center">
                <InstrumentCategoryFilterTableHeader proofreadEventFilterSet={proofreadEventFilterSet} />
              </ExTableCell>
              <ExTableCell align="center">委託先</ExTableCell>
              <ExTableCell align="center">担当者</ExTableCell>
              <ExTableCell align="center">部屋</ExTableCell>
              <ExTableCell align="center">校正月</ExTableCell>
              <ExTableCell align="center">校正周期</ExTableCell>
              <ExTableCell align="center">校正価格</ExTableCell>
              <OrderTableCell orderAttr="repairState" orderSet={props.indexApi.orderSet} align="center" width={120}>
                修理状況
              </OrderTableCell>
              <ExTableCell width={120} />
              <ExTableCell width={120} />
              <ExTableCell width={120} />
            </TableRow>
          </TableHead>
          <ExTableBody loading={props.indexApi.loading}>
            {props.indexApi.response.proofreadEvents.map((event: ProofreadEvent) => {
              const vm = new ProofreadEventViewModel(event);
              return (
                <ExTableRow key={event.id}>
                  <CheckboxTableCell checkboxId={event.id!} checkboxesSet={checkboxSet} />
                  <ExTableCell width={100}>
                    <ImageView maxHeight={100} maxWidth={100} src={vm.getInstrumentImage()} />
                  </ExTableCell>
                  <ExTableCell>
                    {vm.getInstrumentInfo()}
                    <TableExAttributesInfo tableExAttrs={event.tableExAttrs || []} id={event.id} />
                  </ExTableCell>
                  <ExTableCell align="center">{vm.getInstrumentCategoryName()}</ExTableCell>
                  <ExTableCell align="center">{vm.getSupplier()}</ExTableCell>
                  <ExTableCell align="center">{vm.getUser()}</ExTableCell>
                  <ExTableCell align="center">{vm.getRoom()}</ExTableCell>
                  <ExTableCell align="center">{vm.getScheduledDate()}</ExTableCell>
                  <ExTableCell align="center">{vm.getCycle()}</ExTableCell>
                  <ExTableCell align="center">{vm.getPrice()}</ExTableCell>
                  <ExTableCell align="center">{vm.getRepairLabel()}</ExTableCell>
                  <ExTableCell>
                    <ThemedButton color="success" onClick={() => props.onClickExAttr(event)}>
                      管理情報
                    </ThemedButton>
                  </ExTableCell>
                  <ExTableCell>
                    <Tooltip title={!vm.canMakeResult() ? '修理が完了していないため校正結果を入力できません' : ''}>
                      <div>
                        <ThemedButton
                          color="primary"
                          disabled={!vm.canMakeResult()}
                          onClick={() => handleClickResult(event)}
                        >
                          結果入力
                        </ThemedButton>
                      </div>
                    </Tooltip>
                  </ExTableCell>
                  <ExTableCell>
                    {event.defectReport ? (
                      <Tooltip title={!currentUser.authority?.repair ? '修理管理権限がないため使用できません' : ''}>
                        <div>
                          <ThemedButton
                            variant="outlined"
                            color="error"
                            onClick={() => handleClickReport(event)}
                            disabled={!currentUser.authority?.repair}
                          >
                            不具合詳細
                          </ThemedButton>
                        </div>
                      </Tooltip>
                    ) : (
                      <ThemedButton color="error" onClick={() => handleClickReport(event)}>
                        不具合報告
                      </ThemedButton>
                    )}
                  </ExTableCell>
                </ExTableRow>
              );
            })}
          </ExTableBody>
        </Table>
      </TableContainer>
      <Pagination response={props.indexApi.response} pageSet={props.indexApi.pageSet} />
      <CheckboxFooter
        checkboxSet={checkboxSet}
        content={
          <>
            <ThemedButton
              color="success"
              onClick={() => props.onClickBulkExAttr(checkboxSet.checkedIds)}
              style={{ marginRight: 12 }}
            >
              管理情報を一括更新
            </ThemedButton>
            <ThemedButton
              color="primary"
              onClick={() => handleClickBulkResult(checkboxSet.checkedIds)}
              style={{ marginRight: 12 }}
            >
              結果を一括入力
            </ThemedButton>
          </>
        }
      />
    </>
  );
};

export default AttachedResultEventTable;
